/**
 * Phase 6 Plan 02 — ActiveFilterChips (D-132).
 *
 * Row of chips showing each active filter dimension with an × removable
 * button (calls removeKey from filterLogic). "Clear all" compact button at
 * end. CONTEXT § Claude's Discretion bullet 4 — ergonomic overflow with
 * "+N more" deferred; simple wrap suffices for v1.
 */
import { FormattedMessage } from "react-intl";
import { X } from "lucide-react";
import { type HistoryFilters, removeKey } from "./filterLogic";

interface ActiveFilterChipsProps {
  filters: HistoryFilters;
   
  onChange: (next: HistoryFilters) => void;
  onClearAll: () => void;
}

interface Chip {
  key: keyof HistoryFilters;
  labelId: string;
}

function activeChips(f: HistoryFilters): Chip[] {
  const chips: Chip[] = [];
  if (f.worker_ids.size > 0) chips.push({ key: "worker_ids", labelId: "HIST_FILTER_BY_WORKER" });
  if (f.date_from !== null) chips.push({ key: "date_from", labelId: "HIST_FILTER_DATE_FROM" });
  if (f.date_to !== null) chips.push({ key: "date_to", labelId: "HIST_FILTER_DATE_TO" });
  if (f.tab1_ticks.size > 0) chips.push({ key: "tab1_ticks", labelId: "HIST_FILTER_TAB1_HEADER" });
  if (f.tab2_ticks.size > 0) chips.push({ key: "tab2_ticks", labelId: "HIST_FILTER_TAB2_HEADER" });
  if (f.coatings.size > 0) chips.push({ key: "coatings", labelId: "HIST_FILTER_COATING_HEADER" });
  if (f.pm_edited_only) chips.push({ key: "pm_edited_only", labelId: "HIST_FILTER_PM_EDITED_ONLY" });
  if (f.has_photos) chips.push({ key: "has_photos", labelId: "HIST_FILTER_HAS_PHOTOS" });
  if (f.no_photos) chips.push({ key: "no_photos", labelId: "HIST_FILTER_NO_PHOTOS" });
  return chips;
}

export function ActiveFilterChips({
  filters,
  onChange,
  onClearAll,
}: ActiveFilterChipsProps) {
  const chips = activeChips(filters);
  if (chips.length === 0) return null;
  return (
    <div
      className="flex flex-wrap gap-2 mbs-2"
      data-testid="active-filter-chips"
    >
      {chips.map((c) => (
        <span
          key={c.key as string}
          data-testid={`active-chip-${c.key}`}
          className="inline-flex items-center gap-1 ps-2 pe-2 h-6 rounded-full bg-secondary-container text-xs text-on-secondary-container"
        >
          <FormattedMessage id={c.labelId} />
          <button
            type="button"
            aria-label="remove filter"
            onClick={() => onChange(removeKey(filters, c.key))}
            className="inline-flex items-center justify-center hover:bg-surface-variant rounded-full"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        data-testid="active-chips-clear-all"
        className="inline-flex items-center text-xs underline text-on-surface-variant"
      >
        <FormattedMessage id="HIST_CLEAR_ALL_FILTERS" />
      </button>
    </div>
  );
}
